import type { RouteRecordRaw } from 'vue-router'
import { CAPABILITIES as ADMIN_CAPABILITIES } from '~/modules/admin/nav'
import { CAPABILITIES as AFFILIATE_CAPABILITIES } from '~/modules/affiliate/constants'
import { CAPABILITIES } from '~/modules/landlord/constants'

/**
 * The onboarding flow's routes: **create** a property with its spaces, or **edit** one that
 * exists — its own fields and any spaces to add, in the same four steps. One step per route,
 * so back, forward and refresh all work and a step is shareable.
 *
 * The same page is registered under both workspaces. It reads `meta.workspace` and `meta.mode`
 * to pick the API prefix, the property source and where its links go; nothing else differs.
 *
 * No feature flag. Creating a property now creates its spaces, so hiding this route would hide
 * "Add property" itself — the workspace's own flag is the gate.
 */
const page = () => import('./pages/PropertyOnboardingPage.vue')

const base = { layout: 'dashboard', requiresAuth: true }

/** Earlier flows' step names: Layout became Structure, Spaces became Rent, the rest is Review. */
function renamedStep(step: string | string[]): string {
  return step === 'layout' ? 'structure' : step === 'spaces' ? 'rent' : 'review'
}

export const onboardingRoutes: RouteRecordRaw[] = [
  // A bookmark or a saved link to an earlier step name still lands somewhere sensible.
  {
    path: '/app/landlord/properties/new/:step(layout|spaces|exceptions|pricing)',
    redirect: (to) => `/app/landlord/properties/new/${renamedStep(to.params.step)}`,
  },
  {
    path: '/app/affiliate/landlords/:id(\\d+)/properties/new/:step(layout|spaces|exceptions|pricing)',
    redirect: (to) =>
      `/app/affiliate/landlords/${to.params.id}/properties/new/${renamedStep(to.params.step)}`,
  },
  // "Add spaces" was its own flow at `/onboarding`. It is the Structure step of editing now.
  {
    path: '/app/:workspace(landlord|affiliate)/properties/:id(\\d+)/onboarding',
    redirect: (to) => `/app/${to.params.workspace}/properties/${to.params.id}/edit/structure`,
  },
  {
    path: '/app/:workspace(landlord|affiliate)/properties/:id(\\d+)/onboarding/:step(layout|spaces|exceptions|pricing|structure|rent|review)',
    redirect: (to) => {
      const step = String(to.params.step)
      const target = ['structure', 'rent', 'review'].includes(step) ? step : renamedStep(step)
      return `/app/${to.params.workspace}/properties/${to.params.id}/edit/${target}`
    },
  },
  // ---- Landlord ------------------------------------------------------------------------
  {
    path: '/app/landlord/properties/new',
    redirect: '/app/landlord/properties/new/details',
  },
  {
    // Before `/properties/:id` in the landlord module: "new" must never be read as an id.
    path: '/app/landlord/properties/new/:step(details|structure|rent|review)',
    name: 'landlord-property-new',
    component: page,
    meta: {
      ...base,
      workspace: 'landlord' as const,
      mode: 'create' as const,
      capability: CAPABILITIES.propertyCreate,
      title: 'landlord.nav.properties',
    },
  },
  {
    path: '/app/landlord/properties/:id(\\d+)/edit',
    redirect: (to) => `/app/landlord/properties/${to.params.id}/edit/details`,
  },
  {
    path: '/app/landlord/properties/:id(\\d+)/edit/:step(details|structure|rent|review)',
    name: 'landlord-property-edit',
    component: page,
    meta: {
      ...base,
      workspace: 'landlord' as const,
      mode: 'extend' as const,
      capability: CAPABILITIES.propertyUpdate,
      title: 'landlord.nav.properties',
    },
  },
  // ---- Affiliate (Phase 06: the officer's copy) ----------------------------------------
  {
    path: '/app/affiliate/landlords/:id(\\d+)/properties/new',
    redirect: (to) => `/app/affiliate/landlords/${to.params.id}/properties/new/details`,
  },
  {
    path: '/app/affiliate/landlords/:id(\\d+)/properties/new/:step(details|structure|rent|review)',
    name: 'affiliate-landlord-property-new',
    component: page,
    meta: {
      ...base,
      workspace: 'affiliate' as const,
      mode: 'create' as const,
      capability: AFFILIATE_CAPABILITIES.propertyCreate,
      title: 'affiliate.nav.properties',
    },
  },
  {
    path: '/app/affiliate/properties/:id(\\d+)/edit',
    redirect: (to) => `/app/affiliate/properties/${to.params.id}/edit/details`,
  },
  {
    path: '/app/affiliate/properties/:id(\\d+)/edit/:step(details|structure|rent|review)',
    name: 'affiliate-property-edit',
    component: page,
    meta: {
      ...base,
      workspace: 'affiliate' as const,
      mode: 'extend' as const,
      capability: AFFILIATE_CAPABILITIES.propertyUpdate,
      title: 'affiliate.nav.properties',
    },
  },
  // ---- Administrator (2026-10-27, later: the console's property edit) ---------------
  {
    path: '/app/admin/properties/:id(\\d+)/edit',
    redirect: (to) => `/app/admin/properties/${to.params.id}/edit/details`,
  },
  {
    path: '/app/admin/properties/:id(\\d+)/edit/:step(details|structure|rent|review)',
    name: 'admin-property-edit',
    component: page,
    meta: {
      ...base,
      workspace: 'admin' as const,
      mode: 'extend' as const,
      capability: ADMIN_CAPABILITIES.adminAccess,
      title: 'admin.nav.properties',
    },
  },
]
